import { useEffect, useSyncExternalStore } from "react"
import type { ReactNode } from "react"
import { createPortal } from "react-dom"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import { PlanLimitBanner } from "@/components/onboarding/PlanLimitBanner"
import type { Hosted, HostedScreen } from "@/lib/plan-limit-host"
import { closeHostedPlanLimit, hostedPlanLimit, subscribeHostedPlanLimit } from "@/lib/plan-limit-host"

function HostedScreenView({ container, title, banner }: Hosted): ReactNode {
  // La fenêtre emporte l'écran quand elle quitte la page.
  // Pendant son animation de sortie (`data-state="closed"`), elle reste à l'écran.
  useEffect(() => {
    const closeWhenGone = () => {
      if (!container.isConnected) closeHostedPlanLimit()
    }
    closeWhenGone()
    const observer = new MutationObserver(closeWhenGone)
    observer.observe(container.ownerDocument.body, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [container])
  return (
    <div
      role="alert"
      data-test="plan-limit-dialog"
      className="sticky bottom-0 z-10 space-y-2 rounded-lg border bg-background p-3 shadow-md"
    >
      <p className="text-sm font-semibold">{title}</p>
      <PlanLimitBanner {...banner} />
    </div>
  )
}

/**
 * Affiche l'écran de limite dans la fenêtre modale ouverte.
 * À monter une fois, à côté de `Toaster`.
 *
 * @example <PlanLimitDialogHost /> // l'écran apparaît dans la fenêtre ouverte, null sinon
 */
export function PlanLimitDialogHost(): ReactNode {
  const current = useSyncExternalStore(subscribeHostedPlanLimit, hostedPlanLimit)
  return pipe(
    current,
    O.match<HostedScreen, ReactNode>(
      () => null,
      ({ id, container, title, banner }) =>
        createPortal(<HostedScreenView key={id} container={container} title={title} banner={banner} />, container),
    ),
  )
}
